import React, { useState } from "react";
import {
  Hash,
  Calendar,
  CalendarClock,
  Tag,
  Sliders,
  CheckCircle2,
} from "lucide-react";
import Honeypot from "@/components/Honeypot";
import { submitLead, baseMeta } from "@/lib/api";

const APP_ICONS = [
  { key: "batch", label: "Batch Information", Icon: Hash },
  { key: "mfg", label: "Manufacturing Date", Icon: Calendar },
  { key: "exp", label: "Expiry Date", Icon: CalendarClock },
  { key: "lot", label: "Lot Identification", Icon: Tag },
  { key: "var", label: "Variable Information", Icon: Sliders },
];

const AppIcons = () => (
  <section
    className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)]"
    data-testid="coding-app-icons"
  >
    <p className="eyebrow">Coding Applications</p>
    <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
      Common coding & marking use cases
    </h2>
    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
      {APP_ICONS.map(({ key, label, Icon }) => (
        <div key={key} className="flex flex-col items-start" data-testid={`coding-icon-${key}`}>
          <Icon
            strokeWidth={1.5}
            style={{ width: 36, height: 36, color: "var(--pharma-blue)" }}
            aria-hidden="true"
          />
          <p className="font-heading mt-4 text-[15px] font-semibold text-[var(--navy)]">
            {label}
          </p>
        </div>
      ))}
    </div>
  </section>
);

const initialForm = {
  machineBrand: "",
  printerModel: "",
  existingCartridge: "",
  currentInkType: "",
  substrate: "",
  printRequirement: "",
  productionSpeed: "",
  email: "",
  contactName: "",
  company: "",
  country: "",
};

const Field = ({ label, name, type = "text", value, onChange, required, options }) => {
  const id = `qf-${name}`;
  const common =
    "w-full border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5"
      >
        {label} {required && <span className="text-[var(--pharma-blue)]">*</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={3}
          className={common}
          data-testid={`qf-${name}`}
        />
      ) : type === "select" ? (
        <select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className={common}
          data-testid={`qf-${name}`}
        >
          <option value="">Select…</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          className={common}
          data-testid={`qf-${name}`}
          autoComplete="off"
        />
      )}
    </div>
  );
};

const Form = () => {
  const [form, setForm] = useState(initialForm);
  const [hp, setHp] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const res = await submitLead({
      type: "TECHNICAL_ENQUIRY",
      source_page: "/solutions/coding-marking",
      contact: {
        name: form.contactName,
        company: form.company,
        email: form.email,
        country: form.country,
      },
      attribution: { solution_slug: "coding-marking" },
      meta: baseMeta(),
      payload: {
        machine_brand: form.machineBrand || null,
        machine_model: form.printerModel || null,
        substrate: form.substrate || null,
        current_ink: [form.existingCartridge, form.currentInkType].filter(Boolean).join(" · ") || null,
        print_requirement: form.printRequirement || null,
        production_speed: form.productionSpeed || null,
        issue_or_requirement:
          form.printRequirement ||
          "Coding requirement qualification submitted from /solutions/coding-marking",
      },
      _hp: hp,
    });
    if (res.ok) setSubmitted(true);
    else if (res.status === 429) setError("You are submitting too frequently. Please try again in a minute.");
    else setError("Please check the required fields and try again.");
  };

  return (
    <section
      className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20 border-t border-[var(--border)]"
      data-testid="coding-qualification"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 lg:gap-16 items-start">
        <div>
          <p className="eyebrow">Qualification</p>
          <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
            Coding requirement qualification
          </h2>
          <p className="mt-4 text-[15.5px] leading-relaxed text-[var(--charcoal)]">
            Share your machine, substrate and print requirement so we can review
            whether an HP-compatible cartridge from our range is a suitable
            starting point for evaluation.
          </p>
          <p className="mt-4 text-[13px] italic text-[var(--slate)]">
            Suitability is confirmed through application review; no performance
            specifications are inferred from this form.
          </p>
        </div>

        {submitted ? (
          <div
            className="border border-[var(--border)] rounded-[4px] p-8 bg-[var(--bg-alt)]"
            data-testid="qf-success"
            role="status"
          >
            <CheckCircle2
              strokeWidth={1.5}
              style={{ width: 40, height: 40, color: "var(--pharma-blue)" }}
              aria-hidden="true"
            />
            <p className="font-heading mt-4 text-[19px] font-semibold text-[var(--navy)]">
              Thank you — your coding requirement has been received.
            </p>
            <p className="mt-3 text-[14.5px] text-[var(--charcoal)]">
              A member of our technical team will review the details and follow
              up shortly.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="border border-[var(--border)] rounded-[4px] p-6 lg:p-8"
            data-testid="qf-form"
            noValidate
          >
            {error && (
              <div className="mb-4 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="qf-error">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Machine Brand" name="machineBrand" value={form.machineBrand} onChange={onChange} />
              <Field label="Printer Model" name="printerModel" value={form.printerModel} onChange={onChange} />
              <Field label="Existing Cartridge" name="existingCartridge" value={form.existingCartridge} onChange={onChange} />
              <Field
                label="Current Ink Type"
                name="currentInkType"
                type="select"
                options={["Solvent", "Water-based", "Other"]}
                value={form.currentInkType}
                onChange={onChange}
              />
              <Field label="Substrate" name="substrate" value={form.substrate} onChange={onChange} />
              <Field label="Production Speed" name="productionSpeed" value={form.productionSpeed} onChange={onChange} />
              <div className="sm:col-span-2">
                <Field
                  label="Print Requirement"
                  name="printRequirement"
                  type="textarea"
                  value={form.printRequirement}
                  onChange={onChange}
                />
              </div>
              <Field label="Contact Name" name="contactName" required value={form.contactName} onChange={onChange} />
              <Field label="Business Email" name="email" type="email" required value={form.email} onChange={onChange} />
              <Field label="Company" name="company" required value={form.company} onChange={onChange} />
              <Field label="Country" name="country" required value={form.country} onChange={onChange} />
            </div>
            <div className="mt-8">
              <button
                type="submit"
                className="btn-primary text-sm"
                data-testid="qf-submit"
              >
                Discuss Coding Requirement
              </button>
            </div>
            <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
          </form>
        )}
      </div>
    </section>
  );
};

const CodingMarkingExtras = { AppIcons, Form };
export default CodingMarkingExtras;
